// ---------------------------------------------------------------------------
// PlanogramGridOps
//
// Pure functions operating on a PlanogramGrid (no React, no class state).
// This is the algorithmic core that used to live inline inside
// PlanogramDetails.tsx's handleMergeRight/handleMergeLeft/handleMergeSelected/
// handleUnmerge/handleClearCell/handleDeleteCell/handleSchemeSettingsSave.
//
// Kept as standalone functions (not methods) so they can be unit tested in
// total isolation — give them a grid, get back a new grid, assert on shape.
// PlanogramEditor (the stateful OOP wrapper) calls into these; it does not
// reimplement them.
//
// Convention: every function here returns a NEW grid (cells are copied at
// the row level — `grid.map(row => [...row])` — before any mutation), never
// mutates its input. This mirrors the copy-on-write pattern already used
// throughout the original handlers (`const updatedCells = [...row]`) but
// makes it a hard rule enforced in one place instead of "remembered" in 15
// different handlers.
// ---------------------------------------------------------------------------

import { createEmptyCell, PlanogramCellModel } from '../../model/PlanogramCellModel';
import { PlanogramCellData, PlanogramGrid, MergeData } from './types';

const cloneGrid = (grid: PlanogramGrid): PlanogramGrid => grid.map(row => [...row]);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const asCellData = (v: any): PlanogramCellData => v as unknown as PlanogramCellData;

export class PlanogramGridOps {
    // -----------------------------------------------------------------------
    // Cell-level mutations
    // -----------------------------------------------------------------------

    static updateCellField(
        grid: PlanogramGrid,
        rowIndex: number,
        colIndex: number,
        field: string,
        value: unknown,
    ): PlanogramGrid {
        if (!grid?.[rowIndex]?.[colIndex]) {
            return grid;
        }

        const next = cloneGrid(grid);
        const current =
            next[rowIndex][colIndex];

        const currentProduct =
            current.product as
                | Record<string, unknown>
                | null;

        /*
        * The value column accepts only a numeric value. The independently
        * editable unit is preserved while this value changes.
        */
        if (field === 'weightVolume') {
            const normalizedValue =
                String(value ?? '')
                    .trim()
                    .replace(',', '.');

            const numericValue =
                normalizedValue
                    ? Number(normalizedValue)
                    : null;

            const parsedValue =
                numericValue !== null &&
                Number.isFinite(numericValue)
                    ? numericValue
                    : null;

            const currentStructuredValue =
                current.weightOrVolume &&
                typeof current.weightOrVolume ===
                    'object'
                    ? current.weightOrVolume as {
                        value?: unknown;
                        unit?: unknown;
                    }
                    : null;

            const productStructuredValue =
                currentProduct
                    ?.weightOrVolume &&
                typeof currentProduct
                    .weightOrVolume ===
                    'object'
                    ? currentProduct
                        .weightOrVolume as {
                            value?: unknown;
                            unit?: unknown;
                        }
                    : null;

            const unit = String(
                currentStructuredValue?.unit ??
                    current
                        .unitOfMeasurement ??
                    productStructuredValue?.unit ??
                    currentProduct
                        ?.unitOfMeasurement ??
                    '',
            ).trim();

            const weightOrVolume =
                parsedValue !== null &&
                unit
                    ? {
                        value: parsedValue,
                        unit,
                    }
                    : null;

            next[rowIndex][colIndex] =
                asCellData(
                    new PlanogramCellModel({
                        ...current,

                        weightOrVolume,
                        weightVolume:
                            parsedValue,

                        /*
                        * Preserve the unit even after clearing the numeric
                        * value. This allows the user to enter another number
                        * without having to select the product again.
                        */
                        unitOfMeasurement:
                            unit || null,

                        product:
                            current.product
                                ? {
                                    ...current.product,
                                    weightOrVolume,
                                    weightVolume:
                                        parsedValue,
                                    unitOfMeasurement:
                                        unit ||
                                        null,
                                }
                                : current.product,
                    }),
                );

            return next;
        }

        /*
        * Supports callers that provide the complete structured value.
        */
        if (field === 'weightOrVolume') {
            const structuredValue =
                value &&
                typeof value === 'object'
                    ? value as Record<
                        string,
                        unknown
                    >
                    : null;

            const rawNumericValue =
                structuredValue?.value;

            const numericValue =
                typeof rawNumericValue ===
                    'number'
                    ? rawNumericValue
                    : typeof rawNumericValue ===
                        'string'
                        ? Number(
                            rawNumericValue
                                .replace(',', '.'),
                        )
                        : null;

            const parsedValue =
                numericValue !== null &&
                Number.isFinite(numericValue)
                    ? numericValue
                    : null;

            const unit = String(
                structuredValue?.unit ??
                    current
                        .unitOfMeasurement ??
                    currentProduct
                        ?.unitOfMeasurement ??
                    '',
            ).trim();

            const weightOrVolume =
                parsedValue !== null &&
                unit
                    ? {
                        value: parsedValue,
                        unit,
                    }
                    : null;

            next[rowIndex][colIndex] =
                asCellData(
                    new PlanogramCellModel({
                        ...current,
                        weightOrVolume,
                        weightVolume:
                            weightOrVolume?.value ??
                            null,
                        unitOfMeasurement:
                            unit || null,
                        product:
                            current.product
                                ? {
                                    ...current.product,
                                    weightOrVolume,
                                    weightVolume:
                                        weightOrVolume
                                            ?.value ??
                                        null,
                                    unitOfMeasurement:
                                        unit ||
                                        null,
                                }
                                : current.product,
                    }),
                );

            return next;
        }

        /*
        * Recalculate the structured API value when the separate unit
        * dropdown changes.
        */
        if (
            field ===
            'unitOfMeasurement'
        ) {
            const selectedUnit =
                value && typeof value === 'object'
                    ? value as Record<string, unknown>
                    : null;

            const unit = String(
                selectedUnit?.value ??
                selectedUnit?.id ??
                selectedUnit?.code ??
                value ??
                '',
            ).trim();

            const currentNumericValue =
                typeof current.weightVolume ===
                    'number'
                    ? current.weightVolume
                    : Number(
                        String(
                            current.weightVolume ??
                            currentProduct
                                ?.weightVolume ??
                            '',
                        ).replace(',', '.'),
                    );

            const parsedValue =
                Number.isFinite(
                    currentNumericValue,
                )
                    ? currentNumericValue
                    : null;

            const weightOrVolume =
                parsedValue !== null &&
                unit
                    ? {
                        value: parsedValue,
                        unit,
                    }
                    : null;

            next[rowIndex][colIndex] =
                asCellData(
                    new PlanogramCellModel({
                        ...current,
                        weightOrVolume,
                        weightVolume:
                            parsedValue,
                        unitOfMeasurement:
                            unit || null,
                        product:
                            current.product
                                ? {
                                    ...current.product,
                                    weightOrVolume,
                                    weightVolume:
                                        parsedValue,
                                    unitOfMeasurement:
                                        unit ||
                                        null,
                                }
                                : current.product,
                    }),
                );

            return next;
        }

        /*
        * Generic field handling.
        */
        next[rowIndex][colIndex] =
            asCellData(
                new PlanogramCellModel({
                    ...current,
                    [field]: value,
                    product:
                        current.product
                            ? {
                                ...current.product,
                                [field]: value,
                            }
                            : current.product,
                }),
            );

        return next;
    }

    static deleteCell(grid: PlanogramGrid, rowIndex: number, colIndex: number): PlanogramGrid {
        const next = cloneGrid(grid);
        const target = next[rowIndex][colIndex];
        const { mdbId, evaDtsId } = target ?? {};

        next[rowIndex][colIndex] = createEmptyCell({ rowIndex, colIndex }) as unknown as PlanogramCellData;
        next[rowIndex][colIndex].mdbId = mdbId;
        next[rowIndex][colIndex].evaDtsId = evaDtsId;

        return next;
    }

    /** Clears a cell (and, if it's the source of a merge block, every cell in that block). */
    static clearCell(grid: PlanogramGrid, rowIndex: number, colIndex: number): PlanogramGrid {
        const next = cloneGrid(grid);
        const cell = next[rowIndex]?.[colIndex];
        if (!cell) return grid;

        const colspan = cell.merged && (cell.mergeData?.colspan ?? 1) > 1 ? cell.mergeData!.colspan! : 1;

        for (let i = 0; i < colspan; i++) {
            const cIdx = colIndex + i;
            const target = next[rowIndex][cIdx];
            const { mdbId, evaDtsId, externalId } = target;

            next[rowIndex][cIdx] = asCellData(new PlanogramCellModel({ ...target } as any).resetToEmpty());
            next[rowIndex][cIdx].mdbId = mdbId;
            next[rowIndex][cIdx].evaDtsId = evaDtsId;
            next[rowIndex][cIdx].externalId = externalId;
        }

        return next;
    }

    static clearCells(grid: PlanogramGrid, keys: Array<{ rowIndex: number; colIndex: number }>, columns: number): PlanogramGrid {
        let next = cloneGrid(grid);

        keys.forEach(({ rowIndex, colIndex }) => {
            const cell = next[rowIndex][colIndex];

            if (cell.merged && (cell.mergeData?.colspan ?? 1) > 1) {
                const startColIndex = cell.mergeData?.sourceCell?.colIndex ?? colIndex;
                const colspan = cell.mergeData!.colspan!;

                for (let i = 0; i < colspan; i++) {
                    const targetColIndex = startColIndex + i;
                    if (targetColIndex < columns) {
                        const targetCell = next[rowIndex][targetColIndex];
                        const { mdbId, evaDtsId } = targetCell ?? {};
                        next[rowIndex][targetColIndex] = createEmptyCell({
                            rowIndex,
                            colIndex: targetColIndex,
                        }) as unknown as PlanogramCellData;
                        next[rowIndex][targetColIndex].mdbId = mdbId;
                        next[rowIndex][targetColIndex].evaDtsId = evaDtsId;
                    }
                }
            } else {
                const targetCell = next[rowIndex][colIndex];
                const { mdbId, evaDtsId } = targetCell ?? {};
                next[rowIndex][colIndex] = createEmptyCell({ rowIndex, colIndex }) as unknown as PlanogramCellData;
                next[rowIndex][colIndex].mdbId = mdbId;
                next[rowIndex][colIndex].evaDtsId = evaDtsId;
            }
        });

        return next;
    }

    static duplicateCell(
        grid: PlanogramGrid,
        sourceRowIndex: number,
        sourceColIndex: number,
        targetRowIndex: number,
        targetColIndex: number,
    ): PlanogramGrid {
        if (!grid?.[sourceRowIndex]?.[sourceColIndex]) return grid;
        if (!grid?.[targetRowIndex]?.[targetColIndex]) return grid;

        const sourceCell = grid[sourceRowIndex][sourceColIndex];
        const targetCell = grid[targetRowIndex][targetColIndex];
        const next = cloneGrid(grid);

        next[targetRowIndex][targetColIndex] = {
            ...targetCell,
            type: sourceCell.type,
            product: sourceCell.product ? { ...sourceCell.product } : null,
            price: sourceCell.price,
            minQuantity: sourceCell.minQuantity,
            maxQuantity: sourceCell.maxQuantity,
            categoryColor: sourceCell.categoryColor,
            currency: sourceCell.currency,
            category: sourceCell.category,
            categoryName: sourceCell.categoryName,
            productName: sourceCell.productName,
            ean: sourceCell.ean,
            hasPhoto: sourceCell.hasPhoto,
            productPhotoUrl: sourceCell.productPhotoUrl,
            weightOrVolume: sourceCell.weightOrVolume
                ? { ...sourceCell.weightOrVolume }
                : null,
            weightVolume: sourceCell.weightVolume ?? null,
            unitOfMeasurement: sourceCell.unitOfMeasurement ?? null,
            effectiveDate: sourceCell.effectiveDate ?? null,
        };

        return next;
    }

    // -----------------------------------------------------------------------
    // Merge / unmerge
    // -----------------------------------------------------------------------

    private static blockColspan(cell: PlanogramCellData): number {
        return cell.merged && (cell.mergeData?.colspan ?? 1) > 1 ? cell.mergeData!.colspan! : 1;
    }

    private static buildMergedCell(
        rowIndex: number,
        startCol: number,
        colspan: number,
        sourceProductCell: PlanogramCellData,
    ): PlanogramCellModel {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const mergedCell: any = new PlanogramCellModel({
            id: `${rowIndex}-${startCol}`,
            type: 'empty',
            product: null,
            price: null,
            minQuantity: null,
            maxQuantity: null,
            mergeCells: true,
            merged: true,
            mergeData: {
                colspan,
                mergedCells: Array.from({ length: colspan }, (_, i) => `${rowIndex}-${startCol + i}`),
                sourceCell: { rowIndex, colIndex: startCol },
                targetCells: Array.from({ length: colspan - 1 }, (_, i) => ({
                    rowIndex,
                    colIndex: startCol + 1 + i,
                })),
            },
            categoryColor: '',
            currency: null,
            category: null,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any);

        mergedCell.copyProductData(sourceProductCell);
        return mergedCell;
    }

    private static stampMergeFollowers(
        next: PlanogramGrid,
        rowIndex: number,
        startCol: number,
        colspan: number,
    ): void {
        for (let i = 1; i < colspan; i++) {
            const targetColIndex = startCol + i;
            const targetCell = next[rowIndex][targetColIndex];
            next[rowIndex][targetColIndex] = asCellData(new PlanogramCellModel({
                ...targetCell,
                merged: true,
                mergeData: {
                    colspan,
                    mergedCells: Array.from({ length: colspan }, (_, j) => `${rowIndex}-${startCol + j}`),
                    isMerged: true,
                    sourceCell: { rowIndex, colIndex: startCol },
                },
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any));
        }
    }

    /** Merge the block starting at (rowIndex, colIndex) with the block immediately to its right. */
    static mergeRight(grid: PlanogramGrid, rowIndex: number, colIndex: number, totalColumns: number): PlanogramGrid | null {
        if (colIndex >= totalColumns - 1) return null;

        const sourceCell = grid[rowIndex][colIndex];
        const currentBlockStart = colIndex;
        const currentBlockColspan = this.blockColspan(sourceCell);
        const currentBlockEnd = currentBlockStart + currentBlockColspan - 1;

        let rightBlockStart = currentBlockEnd + 1;
        if (rightBlockStart >= totalColumns) return null;

        let rightStartCell = grid[rowIndex][rightBlockStart];
        if (rightStartCell?.merged && rightStartCell.mergeData?.sourceCell) {
            rightBlockStart = rightStartCell.mergeData.sourceCell.colIndex;
            rightStartCell = grid[rowIndex][rightBlockStart];
        }

        const rightBlockColspan = this.blockColspan(rightStartCell);
        const newColspan = rightBlockStart + rightBlockColspan - currentBlockStart;
        if (currentBlockStart + newColspan > totalColumns) return null;

        const next = cloneGrid(grid);
        const mergedCell = this.buildMergedCell(rowIndex, currentBlockStart, newColspan, next[rowIndex][currentBlockStart]);
        next[rowIndex][currentBlockStart] = asCellData(mergedCell);
        this.stampMergeFollowers(next, rowIndex, currentBlockStart, newColspan);

        return next;
    }

    /** Merge the block starting at (rowIndex, colIndex) with the block immediately to its left. */
    static mergeLeft(grid: PlanogramGrid, rowIndex: number, colIndex: number, totalColumns: number): PlanogramGrid | null {
        if (colIndex <= 0) return null;

        const sourceCell = grid[rowIndex][colIndex];
        const currentBlockStart = colIndex;
        const currentBlockColspan = this.blockColspan(sourceCell);
        const currentBlockEnd = currentBlockStart + currentBlockColspan - 1;

        let leftCellIndex = currentBlockStart - 1;
        if (leftCellIndex < 0) return null;

        const immediateLeftCell = grid[rowIndex][leftCellIndex];
        if (immediateLeftCell?.merged && immediateLeftCell.mergeData?.sourceCell) {
            leftCellIndex = immediateLeftCell.mergeData.sourceCell.colIndex;
        }

        const newColspan = currentBlockEnd - leftCellIndex + 1;
        if (leftCellIndex + newColspan > totalColumns) return null;

        const next = cloneGrid(grid);
        const mergedCell = this.buildMergedCell(rowIndex, leftCellIndex, newColspan, next[rowIndex][currentBlockStart]);
        next[rowIndex][leftCellIndex] = asCellData(mergedCell);
        this.stampMergeFollowers(next, rowIndex, leftCellIndex, newColspan);

        return next;
    }

    /** Merge an arbitrary contiguous selection (must all be in the same row, no gaps). */
    static mergeSelected(
        grid: PlanogramGrid,
        selectionKeys: string[],
    ): { grid: PlanogramGrid; error?: 'invalidSelection' } | null {
        if (selectionKeys.length < 2) return null;

        const [firstRow, firstColRaw] = selectionKeys[0].split('-').map(Number);

        const ranges = selectionKeys.map(key => {
            const [r, c] = key.split('-').map(Number);
            const cell = grid[r][c];
            const startCol = cell?.merged && cell.mergeData?.sourceCell ? cell.mergeData.sourceCell.colIndex : c;
            const colspan = this.blockColspan(cell);
            return { row: r, start: startCol, end: startCol + colspan - 1 };
        });

        if (!ranges.every(range => range.row === firstRow)) {
            return { grid, error: 'invalidSelection' };
        }

        const coveredCols = new Set<number>();
        ranges.forEach(({ start, end }) => {
            for (let c = start; c <= end; c++) coveredCols.add(c);
        });
        const minCol = Math.min(...coveredCols);
        const maxCol = Math.max(...coveredCols);

        if (coveredCols.size !== maxCol - minCol + 1) {
            return { grid, error: 'invalidSelection' };
        }

        const startCol = minCol;
        const newColspan = maxCol - minCol + 1;
        const next = cloneGrid(grid);

        const firstSelectedCellRaw = next[firstRow][firstColRaw];
        const firstSelectedSourceCol =
            firstSelectedCellRaw?.merged && firstSelectedCellRaw.mergeData?.sourceCell
                ? firstSelectedCellRaw.mergeData.sourceCell.colIndex
                : firstColRaw;
        const firstSelectedCell = next[firstRow][firstSelectedSourceCol];

        const mergedCell = this.buildMergedCell(firstRow, startCol, newColspan, firstSelectedCell);
        next[firstRow][startCol] = asCellData(mergedCell);
        this.stampMergeFollowers(next, firstRow, startCol, newColspan);

        return { grid: next };
    }

    static unmerge(grid: PlanogramGrid, rowIndex: number, colIndex: number, totalColumns: number): PlanogramGrid | null {
        const clickedCell = grid[rowIndex][colIndex];

        if (!clickedCell.merged || !clickedCell.mergeData?.colspan || clickedCell.mergeData.colspan <= 1) {
            return null;
        }

        const startColIndex = clickedCell.mergeData?.sourceCell?.colIndex ?? colIndex;
        const colspan = clickedCell.mergeData.colspan;
        const next = cloneGrid(grid);

        const startCellBefore = next[rowIndex][startColIndex];
        const baseProductData =
            startCellBefore.type === 'product' && startCellBefore.product
                ? {
                      type: 'product',
                      product: startCellBefore.product,
                      price: startCellBefore.price,
                      minQuantity: startCellBefore.minQuantity,
                      maxQuantity: startCellBefore.maxQuantity,
                      categoryColor: startCellBefore.categoryColor,
                      currency: startCellBefore.currency,
                      category: startCellBefore.category,
                      categoryName: startCellBefore.categoryName,
                      productName: startCellBefore.productName,
                      ean: startCellBefore.ean,
                      hasPhoto: startCellBefore.hasPhoto,
                      productPhotoUrl: startCellBefore.productPhotoUrl,
                      weightOrVolume: startCellBefore.weightOrVolume
                          ? { ...startCellBefore.weightOrVolume }
                          : null,
                      weightVolume: startCellBefore.weightVolume ?? null,
                      unitOfMeasurement: startCellBefore.unitOfMeasurement ?? null,
                      effectiveDate: startCellBefore.effectiveDate ?? null,
                  }
                : null;

        const productPatch = baseProductData ?? {
            type: 'empty',
            product: null,
            price: null,
            minQuantity: null,
            maxQuantity: null,
            categoryColor: null,
            currency: null,
            category: null,
            categoryName: null,
            productName: '',
            ean: null,
            hasPhoto: false,
            productPhotoUrl: null,
            weightOrVolume: null,
            weightVolume: null,
            unitOfMeasurement: null,
            effectiveDate: null,
        };

        const startCell = next[rowIndex][startColIndex];
        next[rowIndex][startColIndex] = asCellData(new PlanogramCellModel({
            ...startCell,
            ...productPatch,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any).resetMergeData());

        for (let i = 1; i < colspan; i++) {
            const targetColIndex = startColIndex + i;
            if (targetColIndex < totalColumns) {
                const targetCell = next[rowIndex][targetColIndex];
                next[rowIndex][targetColIndex] = asCellData(new PlanogramCellModel({
                    ...targetCell,
                    ...productPatch,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } as any).resetMergeData());
            }
        }

        return next;
    }

    static isRowFullyMerged(grid: PlanogramGrid, rowIndex: number, totalColumns: number): boolean {
        const row = grid[rowIndex];
        if (!row || row.length === 0) return false;
        const visibleCells = row.filter(cell => !cell.merged || !cell.mergeData?.isMerged);
        return (
            visibleCells.length === 1 &&
            !!visibleCells[0].merged &&
            visibleCells[0].mergeData?.colspan === totalColumns
        );
    }

    // -----------------------------------------------------------------------
    // Resize
    // -----------------------------------------------------------------------

    static addColumn(grid: PlanogramGrid, columns: number): PlanogramGrid {
        const next = cloneGrid(grid);
        next.forEach((row, rIdx) => {
            row.push(createEmptyCell({ rowIndex: rIdx, colIndex: columns }) as unknown as PlanogramCellData);
        });
        return next;
    }

    static addRow(grid: PlanogramGrid, rows: number, columns: number): PlanogramGrid {
        const next = cloneGrid(grid);
        const newRow: PlanogramCellData[] = [];
        for (let j = 0; j < columns; j++) {
            newRow.push(createEmptyCell({ rowIndex: rows, colIndex: j }) as unknown as PlanogramCellData);
        }
        next.push(newRow);
        return next;
    }

    /** Resize the whole grid to (newRows, newColumns), growing/shrinking and re-clamping merges as needed. */
    static resize(grid: PlanogramGrid, oldRows: number, oldColumns: number, newRows: number, newColumns: number): PlanogramGrid {
        let next = cloneGrid(grid);

        if (newRows > oldRows) {
            for (let i = oldRows; i < newRows; i++) {
                const newRow: PlanogramCellData[] = [];
                for (let j = 0; j < newColumns; j++) {
                    newRow.push(createEmptyCell({ rowIndex: i, colIndex: j }) as unknown as PlanogramCellData);
                }
                next.push(newRow);
            }
        } else if (newRows < oldRows) {
            next = next.slice(0, newRows);
        }

        if (newColumns > oldColumns) {
            next.forEach((row, rIdx) => {
                if (rIdx < oldRows) {
                    for (let j = oldColumns; j < newColumns; j++) {
                        row.push(createEmptyCell({ rowIndex: rIdx, colIndex: j }) as unknown as PlanogramCellData);
                    }
                }
            });
        } else if (newColumns < oldColumns) {
            next.forEach((row, rIdx) => {
                next[rIdx] = row.slice(0, newColumns);
            });

            next.forEach((row, rIdx) => {
                row.forEach((cell, cIdx) => {
                    if (cell.merged && (cell.mergeData?.colspan ?? 1) > 1) {
                        const sourceCol = cell.mergeData?.sourceCell?.colIndex ?? cIdx;
                        const currentColspan = cell.mergeData!.colspan!;
                        const endCol = sourceCol + currentColspan - 1;

                        if (endCol >= newColumns) {
                            const clampedColspan = Math.max(1, newColumns - sourceCol);

                            if (clampedColspan === 1) {
                                next[rIdx][sourceCol] = asCellData(new PlanogramCellModel({
                                    ...cell,
                                    merged: false,
                                    mergeData: null,
                                    mergeCells: false,
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                } as any));
                                for (let i = 1; i < currentColspan && sourceCol + i < newColumns; i++) {
                                    const tci = sourceCol + i;
                                    next[rIdx][tci] = createEmptyCell({ rowIndex: rIdx, colIndex: tci }) as unknown as PlanogramCellData;
                                }
                            } else {
                                next[rIdx][sourceCol] = asCellData(new PlanogramCellModel({
                                    ...cell,
                                    mergeData: {
                                        ...cell.mergeData,
                                        colspan: clampedColspan,
                                        mergedCells: Array.from({ length: clampedColspan }, (_, i) => `${rIdx}-${sourceCol + i}`),
                                        targetCells: Array.from({ length: clampedColspan - 1 }, (_, i) => ({
                                            rowIndex: rIdx,
                                            colIndex: sourceCol + 1 + i,
                                        })),
                                    },
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                } as any));
                                this.stampMergeFollowers(next, rIdx, sourceCol, clampedColspan);
                            }
                        }
                    }
                });
            });
        }

        return next;
    }

    // -----------------------------------------------------------------------
    // Misc grid-state checks
    // -----------------------------------------------------------------------

    static buildDuplicateKeySet(grid: PlanogramGrid, rows: number, columns: number, fieldName: string): Set<string> {
        const valueToKeys = new Map<string, string[]>();
        const duplicateKeys = new Set<string>();

        for (let r = 0; r < rows; r++) {
            const row = grid[r];
            if (!row) continue;
            for (let c = 0; c < columns; c++) {
                const cell = row[c];
                if (!cell) continue;
                const raw = (cell as Record<string, unknown>)[fieldName];
                const value = typeof raw === 'string' ? raw.trim() : (raw != null ? String(raw).trim() : '');
                if (!value) continue;
                const key = `${r}-${c}`;
                if (!valueToKeys.has(value)) {
                    valueToKeys.set(value, [key]);
                } else {
                    valueToKeys.get(value)!.push(key);
                }
            }
        }

        for (const [, keys] of valueToKeys) {
            if (keys.length > 1) keys.forEach(k => duplicateKeys.add(k));
        }

        return duplicateKeys;
    }
}

export type { MergeData };
